'use client'

import { EmptyState, Image } from '@vinted/web-ui'
import { useMemo } from 'react'
import { InView } from 'react-intersection-observer'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getItemsByName,
  ItemPageItemsBlockPluginModel,
  transformItemsBlockItemDtoToProductItem,
} from '@marketplace-web/item-page/item-page-data'

import ItemViewItems from '../../components/ItemViewItems/ItemViewItems'
import ItemsGridSkeleton from '../../components/ItemsGridSkeleton'

const EMPTY_COLLECTION_IMAGE_PATH = 'hanger-empty-state-96.svg'

const ItemPageItemsBlockPlugin = ({
  items,
  contentSource,
  itemId,
  pluginName,
  horizontalScrollEnabled,
}: ItemPageItemsBlockPluginModel & { pluginName: string }) => {
  const asset = useAsset('/assets/no-photo')
  const translate = useTranslate('item.horizontal_items_block_plugin')

  const {
    fetch: fetchItemsByPluginNameData,
    data: loadedData,
    isLoading,
    error,
  } = useFetch(getItemsByName)

  const data = useMemo(() => {
    if (loadedData) {
      return loadedData.items.map(transformItemsBlockItemDtoToProductItem)
    }

    return items
  }, [loadedData, items])

  const handleInViewChange = (inView: boolean) => {
    if (!inView || isLoading || data !== null) return

    fetchItemsByPluginNameData({ itemId, name: pluginName })
  }

  const renderContent = () => {
    if (data === null || isLoading) {
      return <ItemsGridSkeleton />
    }

    if (error || data.length === 0)
      return (
        <EmptyState
          title={translate('empty_state.title')}
          image={<Image src={asset(EMPTY_COLLECTION_IMAGE_PATH)} />}
          testId={`${pluginName}-plugin-empty-state`}
        />
      )

    return (
      <ItemViewItems
        items={data}
        contentSource={contentSource}
        itemTestId={pluginName}
        horizontalScrollEnabled={horizontalScrollEnabled}
      />
    )
  }

  return (
    <InView
      as="div"
      data-testid={`item-page-${pluginName}-plugin`}
      onChange={handleInViewChange}
      rootMargin="0px 0px 60px 0px"
    >
      {renderContent()}
    </InView>
  )
}

export default ItemPageItemsBlockPlugin
